import { CheckIcon, WarningDiamondIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { Merchandise, ProductReference } from "types";
import { VariantQuantity } from "./updateQuantityHelper";
import { CatalogItemImage } from "@/components/Common/CatalogItemImage";
import { Currency } from "@/components/Common/Currency";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { QuantitySpinner } from "@/components/Common/QuantitySpinner/QuantitySpinner";
import { useProduct } from "@/hooks/resources/useProduct";
import {
    getVariantMaxUpdateQuantity,
    getVariantMinQuantity,
    isInventoryTracked,
    isVariantOutOfStock,
    isVariantPublished,
} from "@/models/Merchandise";

interface VariantQuantityListProps {
    variants: VariantQuantity[];
    merchandiseProduct: ProductReference;
    onChange: (templateId: ID, newQuantity: number) => void;
    errorByTemplateId?: Record<string, string[]>;
    successTemplateIds?: string[];
}

export const VariantQuantityList = ({
    variants,
    merchandiseProduct,
    onChange,
    errorByTemplateId = {},
    successTemplateIds = [],
}: VariantQuantityListProps) => {
    const { product: merchandise } = useProduct<Merchandise>(merchandiseProduct.id);
    const successTemplateIdSet = React.useMemo(() => new Set(successTemplateIds), [successTemplateIds]);
    const totalNewQuantity = variants.reduce((sum, variant) => sum + variant.newQuantity, 0);

    if (!merchandise) {
        return null;
    }

    const hasTrackedInventory = isInventoryTracked(merchandise);

    return (
        <div className="space-y-3">
            {variants.map((variant) => (
                <VariantItem
                    key={variant.templateId}
                    variant={variant}
                    totalNewQuantity={totalNewQuantity}
                    merchandise={merchandise}
                    hasTrackedInventory={hasTrackedInventory}
                    errorMessages={errorByTemplateId[variant.templateId] ?? []}
                    isSuccess={successTemplateIdSet.has(variant.templateId)}
                    onChange={onChange}
                />
            ))}
        </div>
    );
};

interface VariantItemProps {
    variant: VariantQuantity;
    totalNewQuantity: number;
    merchandise: Merchandise;
    hasTrackedInventory: boolean;
    errorMessages: string[];
    isSuccess: boolean;
    onChange: (templateId: ID, newQuantity: number) => void;
}

const VariantItem = ({
    variant,
    totalNewQuantity,
    merchandise,
    hasTrackedInventory,
    errorMessages,
    isSuccess,
    onChange,
}: VariantItemProps) => {
    const handleQuantityChange = (newQuantity: number) => {
        onChange(variant.templateId, newQuantity);
    };

    const hasCatalogPhoto = !!variant.catalogItem?.photo;
    const isNoPhysicalStockAvailable =
        !!variant.catalogItem && isVariantOutOfStock(variant.catalogItem, hasTrackedInventory);
    const isAvailable = !!variant.catalogItem && isVariantPublished(variant.catalogItem);
    const isAlreadyPurchased = variant.currentQuantity > 0;

    // Min/max allow updating between minimum required and current purchase + available stock
    const minForVariant = getVariantMinQuantity(variant.newQuantity, totalNewQuantity);
    const maxForVariant = getVariantMaxUpdateQuantity(
        variant.catalogItem,
        hasTrackedInventory,
        variant.isHidden,
        variant.currentQuantity,
    );

    const showSpinner = isAlreadyPurchased || maxForVariant > 0;
    const delta = variant.newQuantity - variant.currentQuantity;

    return (
        <div className="rounded" data-testid="variant-line-item">
            <div className={clsx("flex items-center", { "opacity-50": !isAvailable })}>
                {hasCatalogPhoto && variant.catalogItem ? (
                    <CatalogItemImage catalogItem={variant.catalogItem} size="small" />
                ) : (
                    <ProductImage product={merchandise} size="small" />
                )}

                <div
                    className={clsx("min-w-0 flex-1", {
                        "text-danger": errorMessages.length > 0,
                    })}
                >
                    <NoTranslate className="break-all text-sm font-bold">{variant.variantName}</NoTranslate>
                    <div className="text-sm">
                        {variant.hasPrice ? (
                            <>
                                <Currency>{variant.price}</Currency>/each
                            </>
                        ) : (
                            "No price set"
                        )}
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    {isNoPhysicalStockAvailable && (
                        <Badge size="small" color="danger">
                            Out of Stock
                        </Badge>
                    )}
                    {showSpinner && (
                        <>
                            {isSuccess ? (
                                <div
                                    className="flex h-6 w-6 items-center justify-center rounded-full"
                                    data-testid="variant-success-tick"
                                >
                                    <CheckIcon size="small" className="text-success" />
                                </div>
                            ) : (
                                delta !== 0 && (
                                    <Badge size="small" color="primary">
                                        {delta > 0 && "+"}
                                        {delta}
                                    </Badge>
                                )
                            )}
                            <QuantitySpinner
                                disabled={!isAvailable}
                                value={variant.newQuantity}
                                shouldSyncValue={false}
                                min={minForVariant}
                                max={maxForVariant}
                                minTooltipText="The minimum number of items needed in order to book is"
                                onChange={handleQuantityChange}
                            />
                        </>
                    )}
                </div>
            </div>

            {errorMessages.length > 0 && (
                <div className="mt-1 space-y-1">
                    {errorMessages.map((message) => (
                        <div key={message} className="flex items-start text-danger">
                            <WarningDiamondIcon className="mt-0.5 flex-shrink-0 text-danger" />
                            <span className="ml-1 text-sm">{message}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
